import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

/** Thumbs up/down on one answer. Safe for guests: the run id is unguessable. */
export const rateIntelligenceRun = createServerFn({ method: "POST" })
  .inputValidator((raw: unknown) =>
    z.object({ runId: z.string().uuid(), rating: z.union([z.literal(1), z.literal(-1)]) }).parse(raw),
  )
  .handler(async ({ data }) => {
    const { publicClient } = await import("@/lib/mobile-api.server");
    const { error } = await publicClient().rpc("intelligence_rate_run", {
      _run_id: data.runId,
      _rating: data.rating,
    });
    if (error) return { ok: false };
    return { ok: true };
  });

export interface IntelligenceOverview {
  provider: { transport: string; configured: boolean; primaryModel: string; fastModel: string };
  totals: { runs: number; grounded: number; errors: number; rated: number; positive: number };
  latency: { p50: number; p95: number };
  byRole: { role: string; runs: number; errors: number; avgLatency: number }[];
  byDay: { day: string; runs: number; errors: number }[];
  safety: { flag: string; runs: number }[];
  recent: {
    id: string;
    role: string;
    model: string;
    status: string;
    grounded: boolean;
    latency_ms: number | null;
    tool_calls: number;
    safety_flag: string | null;
    rating: number | null;
    created_at: string;
  }[];
  evals: {
    id: string;
    suite: string;
    case_id: string;
    category: string | null;
    role: string;
    passed: boolean;
    notes: string | null;
    created_at: string;
  }[];
}

/** Admin-only observability read for /admin/intelligence. */
export const getIntelligenceOverview = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<IntelligenceOverview> => {
    const { supabase, userId } = context;
    const { data: isAdmin } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
    if (!isAdmin) throw new Error("Forbidden");

    const { providerHealth } = await import("@/lib/intelligence/provider.server");
    const health = providerHealth();

    const since = new Date(Date.now() - 14 * 24 * 3600 * 1000).toISOString();
    const [{ data: runs }, { data: evals }] = await Promise.all([
      supabase
        .from("intelligence_runs")
        .select(
          "id, role, model, status, grounded, latency_ms, tool_calls, safety_flag, rating, created_at",
        )
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(1000),
      supabase
        .from("intelligence_eval_runs")
        .select("id, suite, case_id, category, role, passed, notes, created_at")
        .order("created_at", { ascending: false })
        .limit(200),
    ]);

    const rows = runs ?? [];
    const latencies = rows.map((r) => r.latency_ms ?? 0).filter((n) => n > 0).sort((a, b) => a - b);
    const pick = (p: number) => (latencies.length ? latencies[Math.min(latencies.length - 1, Math.floor(latencies.length * p))]! : 0);

    const roleMap = new Map<string, { runs: number; errors: number; total: number }>();
    const dayMap = new Map<string, { runs: number; errors: number }>();
    const safetyMap = new Map<string, number>();
    for (const r of rows) {
      const rr = roleMap.get(r.role) ?? { runs: 0, errors: 0, total: 0 };
      rr.runs += 1;
      if (r.status !== "ok") rr.errors += 1;
      rr.total += r.latency_ms ?? 0;
      roleMap.set(r.role, rr);

      const day = r.created_at.slice(0, 10);
      const dd = dayMap.get(day) ?? { runs: 0, errors: 0 };
      dd.runs += 1;
      if (r.status !== "ok") dd.errors += 1;
      dayMap.set(day, dd);

      if (r.safety_flag) safetyMap.set(r.safety_flag, (safetyMap.get(r.safety_flag) ?? 0) + 1);
    }

    return {
      provider: {
        transport: health.transport,
        configured: health.configured,
        primaryModel: health.primaryModel,
        fastModel: health.fastModel,
      },
      totals: {
        runs: rows.length,
        grounded: rows.filter((r) => r.grounded).length,
        errors: rows.filter((r) => r.status !== "ok").length,
        rated: rows.filter((r) => r.rating !== null).length,
        positive: rows.filter((r) => r.rating === 1).length,
      },
      latency: { p50: pick(0.5), p95: pick(0.95) },
      byRole: [...roleMap.entries()].map(([role, v]) => ({
        role,
        runs: v.runs,
        errors: v.errors,
        avgLatency: v.runs ? Math.round(v.total / v.runs) : 0,
      })),
      byDay: [...dayMap.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([day, v]) => ({ day, ...v })),
      safety: [...safetyMap.entries()].map(([flag, runs]) => ({ flag, runs })),
      recent: rows.slice(0, 50),
      evals: evals ?? [],
    };
  });

/** Admin-only: run an evaluation suite and persist the results. */
export const runIntelligenceEvalSuite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) =>
    z
      .object({
        suite: z.enum(["routing", "live"]),
        category: z.string().max(40).nullable(),
        limit: z.number().int().min(1).max(60),
      })
      .parse(raw),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: isAdmin } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
    if (!isAdmin) throw new Error("Forbidden");

    const { runLiveSuite, runRoutingSuite, persistSuite } = await import(
      "@/lib/intelligence/eval-runner.server"
    );
    const summary =
      data.suite === "live"
        ? await runLiveSuite(data.category, data.limit)
        : runRoutingSuite(data.category, data.limit);
    await persistSuite(summary, userId);
    return summary;
  });
